import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  completeSettingsOauth,
  disconnectSettingsService,
  fetchGrowthSettings,
  patchGrowthSettings,
  recomputeAvgPatientValue,
  startSettingsOauth,
  type GrowthSettingsPayload,
  type SettingsOauthService,
} from '../api';

export function useGrowthSettings() {
  return useGrowthQuery({
    queryKey: growthKeys.settings(),
    queryFn: fetchGrowthSettings,
  });
}

export function useGrowthSettingsMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const save = useMutation({
    mutationFn: ({
      payload,
      rawPractice,
    }: {
      payload: Partial<GrowthSettingsPayload>;
      rawPractice: Record<string, unknown>;
    }) => patchGrowthSettings(payload, rawPractice),
    onSuccess: (data) => {
      queryClient.setQueryData(growthKeys.settings(), data);
      notify.success('Settings saved');
    },
    onError: (e) => notify.error(e),
  });

  const startOauth = useMutation({
    mutationFn: (service: SettingsOauthService) => startSettingsOauth(service),
    onSuccess: (data) => {
      if (data.authorizationUrl) {
        window.location.assign(data.authorizationUrl);
        return;
      }
      notify.success('OAuth flow started');
    },
    onError: (e) => notify.error(e),
  });

  const completeOauth = useMutation({
    mutationFn: ({
      service,
      code,
      state,
    }: {
      service: SettingsOauthService;
      code: string;
      state: string;
    }) => completeSettingsOauth(service, code, state),
    onSuccess: () => {
      notify.success('Connection linked');
      void queryClient.invalidateQueries({ queryKey: growthKeys.settings() });
    },
    onError: (e) => notify.error(e),
  });

  const disconnect = useMutation({
    mutationFn: disconnectSettingsService,
    onSuccess: () => {
      notify.success('Connection disconnected');
      void queryClient.invalidateQueries({ queryKey: growthKeys.settings() });
    },
    onError: (e) => notify.error(e),
  });

  const recomputeApv = useMutation({
    mutationFn: recomputeAvgPatientValue,
    onSuccess: () => {
      notify.success('Average patient value recomputed');
      void queryClient.invalidateQueries({ queryKey: growthKeys.settings() });
    },
    onError: (e) => notify.error(e),
  });

  return { save, startOauth, completeOauth, disconnect, recomputeApv };
}
